'use client';

import { useState, type ReactNode, type KeyboardEvent } from 'react';
import { Info } from 'lucide-react';
import { BATTERY_REACH_UNAVAILABLE_COPY, RANGE_RING_RADII_M, formatRingLabel } from '@/lib/range-rings';
import { MAP_LEGEND } from '@/lib/copy';
import type { CooperativeTraffic, DroneMapMission, RcController, SiteMarker } from './droneMapTypes';

const PANEL_ID = 'map-legend-panel';

/** Only the view flags that change what the map actually draws. */
interface MapLegendViewState {
  airspace: boolean;
  missions: boolean;
  altitudeLabels: boolean;
  rangeRings: boolean;
}

/** Which handover looks are on the map right now. */
export interface HandoverKeyState {
  /** Cyan: a handover counting down (or about to). */
  live: boolean;
  /** Red, ringed: a handover that needs an operator to look (D17). */
  attention: boolean;
}

/** No handover line on the map: the default, and a stable identity. */
export const NO_HANDOVERS: HandoverKeyState = Object.freeze({ live: false, attention: false });

interface MapLegendProps {
  viewState: MapLegendViewState;
  missions: DroneMapMission[];
  trailCount: number;
  controllers: RcController[];
  cooperativeTraffic: CooperativeTraffic[];
  sites: SiteMarker[];
  dockCount: number;
  triggerCount: number;
  /**
   * Which handover looks are on the map right now - cyan counting, or red and ringed for one that
   * needs attention (D17). A row shows only while its look is drawn: on a line with no number the
   * colour and ring are the whole message, so each needs its key.
   */
  handoverKey?: HandoverKeyState;
  /**
   * Rendered inside the key's pill, to the right of the button - the scale bar
   * in the live COP. Two readouts that each carried a full pill's worth of
   * border, blur and shadow over the air picture now share one, and the corner
   * gives back the 44px row the old labelled trigger occupied.
   */
  children?: ReactNode;
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[9px] uppercase tracking-wider text-muted-foreground/70">{title}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{children}</div>
    </div>
  );
}

function Row({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {swatch}
      <span>{label}</span>
    </div>
  );
}

/**
 * The map's symbol key. Collapsed by default to a single 28px glyph in the
 * corner pill it shares with the scale bar - operators learn the symbology
 * once, so the key is reference material consulted on demand, not permanent
 * furniture over the air picture.
 *
 * Groups render only when their symbols are actually on the map. ALTITUDE is
 * the notable one: altitude band colors are applied to drone labels only when
 * showAltitudeLabels is on (see applyAltitudeLabelStyle in droneMapLayers.ts),
 * and that defaults to off - so those four rows were decoding a color scheme
 * that wasn't on screen.
 */
export function MapLegend({
  viewState,
  missions,
  trailCount,
  controllers,
  cooperativeTraffic,
  sites,
  dockCount,
  triggerCount,
  handoverKey = NO_HANDOVERS,
  children,
}: MapLegendProps) {
  const [expanded, setExpanded] = useState(false);

  const showMissionRoutes = missions.length > 0 && viewState.missions;
  const hasTracks =
    trailCount > 0 ||
    controllers.length > 0 ||
    cooperativeTraffic.length > 0 ||
    sites.length > 0 ||
    dockCount > 0 ||
    triggerCount > 0;
  const hasHandovers = handoverKey.live || handoverKey.attention;
  const hasOverlays = viewState.airspace || showMissionRoutes || viewState.rangeRings || hasHandovers;

  // Element-local, not a window listener - Escape stays available to the map
  // context menu, which registers its own handler.
  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape' && expanded) {
      e.stopPropagation();
      setExpanded(false);
    }
  }

  return (
    // Unpositioned: this is a flow child of the bottom-left stack in
    // DroneMapControls, which owns the corner inset and the z-20 that keeps a
    // user-invoked disclosure above the passive info panels sharing this
    // corner, and below the z-50 context menus and dock-launch review step.
    <section
      className="text-[10px] text-muted-foreground"
      aria-label={MAP_LEGEND.title}
      onKeyDown={handleKeyDown}
    >
      {/* Rendered even when collapsed so aria-controls always resolves. */}
      <div
        id={PANEL_ID}
        hidden={!expanded}
        className="pointer-events-auto mb-1 max-h-[min(60vh,26rem)] w-[min(calc(100vw-1.5rem),20rem)] space-y-2 overflow-y-auto rounded-md border border-subtle bg-popover/90 px-3 py-2 shadow-lg backdrop-blur-sm"
      >
        <Group title={MAP_LEGEND.groups.drones}>
          <Row swatch={<div className="size-2.5 rounded-full bg-status-green-marker" />} label={MAP_LEGEND.rows.flying} />
          <Row swatch={<div className="size-2 rounded-full bg-status-blue-marker" />} label={MAP_LEGEND.rows.grounded} />
          <Row swatch={<div className="size-2.5 rounded-full bg-status-amber-marker" />} label={MAP_LEGEND.rows.warning} />
          <Row
            swatch={<div className="size-2.5 rounded-full border-2 border-status-amber-marker bg-status-amber-marker/25" />}
            label={MAP_LEGEND.rows.stale}
          />
          <Row swatch={<div className="size-2 rounded-full bg-status-gray-marker" />} label={MAP_LEGEND.rows.lost} />
          <Row
            swatch={
              <div className="relative size-3">
                <div className="absolute left-0 top-0 size-1.5 border-l-2 border-t-2 border-white" />
                <div className="absolute right-0 top-0 size-1.5 border-r-2 border-t-2 border-white" />
                <div className="absolute bottom-0 left-0 size-1.5 border-b-2 border-l-2 border-white" />
                <div className="absolute bottom-0 right-0 size-1.5 border-b-2 border-r-2 border-white" />
              </div>
            }
            label={MAP_LEGEND.rows.selected}
          />
        </Group>

        {viewState.altitudeLabels && (
          <Group title={MAP_LEGEND.groups.altitude}>
            <span className="text-status-blue-fg">0–50</span>
            <span className="text-status-green-fg">50–100</span>
            <span className="text-status-amber-fg">100–200</span>
            <span className="text-status-red-fg">&gt;200</span>
          </Group>
        )}

        {hasTracks && (
          <Group title={MAP_LEGEND.groups.tracks}>
            {trailCount > 0 && (
              <Row swatch={<div className="h-0 w-3 border-t border-status-green/30" />} label={MAP_LEGEND.rows.trail} />
            )}
            {controllers.length > 0 && (
              <Row swatch={<div className="size-2 rounded-[1px] bg-muted-foreground" />} label={MAP_LEGEND.rows.controller} />
            )}
            {cooperativeTraffic.length > 0 && (
              <Row
                swatch={<div className="size-2.5 rotate-45 border-[1.5px] border-accent-violet bg-transparent" />}
                label={MAP_LEGEND.rows.traffic}
              />
            )}
            {sites.length > 0 && (
              <Row swatch={<div className="size-2 rounded-full border-[1.5px] border-status-blue bg-transparent" />} label={MAP_LEGEND.rows.site} />
            )}
            {dockCount > 0 && (
              <Row swatch={<div className="size-2.5 rounded-[2px] border-[1.5px] border-status-green bg-transparent" />} label={MAP_LEGEND.rows.dock} />
            )}
            {triggerCount > 0 && (
              <Row
                swatch={<div className="size-2.5 rounded-full border-[1.5px] border-status-red bg-status-red/20" />}
                label={MAP_LEGEND.rows.detection}
              />
            )}
          </Group>
        )}

        {hasOverlays && (
          <Group title={MAP_LEGEND.groups.overlays}>
            {viewState.airspace && (
              <>
                <Row
                  swatch={<div className="h-2 w-3 rounded-sm border border-dashed border-status-blue bg-status-blue/15" />}
                  label={MAP_LEGEND.rows.airspaceClasses}
                />
                <Row swatch={<div className="h-2 w-3 rounded-sm border border-status-red/60 bg-status-red/30" />} label={MAP_LEGEND.rows.tfr} />
                <Row swatch={<span className="text-status-amber-fg">▲</span>} label={MAP_LEGEND.rows.notam} />
              </>
            )}
            {showMissionRoutes && (
              <>
                <Row swatch={<div className="h-0 w-3 border-t-2 border-dashed border-status-cyan" />} label={MAP_LEGEND.rows.scheduled} />
                <Row swatch={<div className="h-0 w-3 border-t-2 border-dashed border-status-green" />} label={MAP_LEGEND.rows.activeRoute} />
              </>
            )}
            {viewState.rangeRings && (
              <>
                <Row
                  swatch={<div className="size-2.5 rounded-full border border-dashed border-white/40" />}
                  label={MAP_LEGEND.rangeRings(RANGE_RING_RADII_M.map((m) => formatRingLabel(m)).join(' / '))}
                />
                <Row
                  swatch={<div className="h-0 w-3 border-t border-dashed border-status-amber" />}
                  label={MAP_LEGEND.rows.transit}
                />
              </>
            )}
            {handoverKey.live && (
              <Row
                swatch={<div className="h-0 w-3 border-t-[3px] border-dotted border-status-cyan" />}
                label={MAP_LEGEND.rows.handover}
              />
            )}
            {handoverKey.attention && (
              <Row
                swatch={<div className="size-2.5 rounded-full border-2 border-status-red bg-transparent" />}
                label={MAP_LEGEND.rows.handoverAttention}
              />
            )}
          </Group>
        )}

        {/* Stated, not omitted. A missing reach ring must not be read as
            "unlimited range" - see batteryReachRadiusM for why one cannot be
            derived honestly from the data this platform holds. */}
        {viewState.rangeRings && (
          <p className="text-[9px] leading-snug text-status-amber-fg/80">
            {BATTERY_REACH_UNAVAILABLE_COPY.no_endurance_model}
          </p>
        )}
      </div>

      {/* One pill for the whole corner: the key's button, then whatever readout
          is passed in (the scale bar). It carries the border, blur and shadow
          once, and it is pointer-events-none like the stack around it - only
          the button opts back in, so the reading beside it stays live map.
          overflow-hidden is what keeps the button's square fill inside the
          pill's rounded corners, and w-fit is what stops the pill stretching to
          the 320px panel above it when the key is open - this section's width
          is its widest child, and a block-level pill would otherwise grow to
          match and trail empty chrome past the scale bar.

          Pinned below the panel so it never moves out from under the cursor
          when the key is toggled. */}
      <div className="pointer-events-none flex h-7 w-fit items-center overflow-hidden rounded-md border border-subtle bg-popover/90 shadow-lg backdrop-blur-sm">
        {/* Icon-only, and 36x26px rather than the 44px-tall labelled pill it
            replaces. That clears WCAG 2.2 SC 2.5.8 Minimum (24x24 CSS px) - the
            pill's overflow-hidden clips it to the 26px between the borders, so
            the height is the tight dimension and the width is what carries the
            target - but it is a step down from the enhanced 44px the old
            trigger had. What the corner buys back is a 44px row of air picture
            and one less slab of chrome.

            It has a resting fill of its own, not just a hover one: hover is
            compiled under `(hover: hover)` and `title` renders nothing on a
            touch screen, so on the RC's screen a glyph that only lights up on
            hover is indistinguishable from the passive readout beside it. The
            fill, the divider the scale bar draws against it, and the pointer
            cursor are what say "control" before anyone commits to a tap. The
            name it lost from the screen is still on aria-label for a screen
            reader and on title for a hover. */}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls={PANEL_ID}
          aria-label={MAP_LEGEND.toggle}
          title={MAP_LEGEND.title}
          // Open is a different treatment, not a darker hover: same-family
          // tints 5 points apart cannot be told apart, and `hover:` at
          // specificity (0,2,0) would have overridden a flat `bg-*` on the open
          // button and made the cue run backwards under the cursor. Hover is
          // therefore only attached while collapsed.
          // The focus ring is INSET on purpose: the pill's overflow-hidden
          // clips a descendant's outline and any outside box-shadow to the
          // padding box, so a default UA outline - the button's only focus
          // indicator - would be shaved off on three sides (SC 2.4.7).
          className={`pointer-events-auto flex h-full w-9 shrink-0 cursor-pointer items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
            expanded
              ? 'bg-accent/30 text-accent-foreground'
              : 'bg-muted/40 hover:bg-accent/15 hover:text-foreground'
          }`}
        >
          <Info className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
        </button>
        {children}
      </div>
    </section>
  );
}
